import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  FiCheck, FiPhone, FiMail, FiMapPin, FiAward, FiTruck, 
  FiShield, FiHeart, FiStar, FiUsers, FiHome
} from 'react-icons/fi';

const AboutShed = () => {
  const values = [
    {
      icon: FiShield,
      title: 'Quality Construction',
      description: 'Every shed is built with premium materials and expert craftsmanship to last for years.'
    },
    {
      icon: FiHeart,
      title: 'Customer First',
      description: 'We treat every customer like family, providing honest advice and exceptional service.'
    },
    {
      icon: FiTruck,
      title: 'Professional Delivery',
      description: 'Free delivery and professional setup on your prepared site. Nationwide dealer network.'
    },
    {
      icon: FiAward,
      title: 'Warranty Protection',
      description: 'All our sheds come with manufacturer warranty coverage for your peace of mind.'
    }
  ];

  const stats = [
    { value: '500+', label: 'Sheds Delivered' },
    { value: '15+', label: 'Years Experience' },
    { value: '100%', label: 'Satisfaction Rate' },
    { value: '50mi', label: 'Delivery Radius' }
  ];

  const team = [
    {
      name: 'Owner',
      role: 'Founder & Owner',
      description: 'With over 15 years in the outdoor building industry, leading ShedX with a passion for quality and customer service.',
      image: null
    }
  ];

  return (
    <>
      <Helmet>
        <title>About ShedX | Quality Storage Buildings & Outdoor Structures</title>
        <meta name="description" content="Learn about ShedX - your trusted source for quality storage sheds, barns, workshops and outdoor buildings. Connecting buyers with licensed dealers nationwide." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-800 to-emerald-900 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }} />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">About ShedX</h1>
              <p className="text-xl text-emerald-100 mb-8">
                Family-owned and operated since 2010, we're dedicated to providing quality storage buildings, 
                workshops and outdoor structures to families and businesses across the United States.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/sheds"
                  className="inline-flex items-center gap-2 bg-white text-emerald-800 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
                >
                  <FiHome className="w-5 h-5" />
                  Browse Sheds
                </Link>
                <a
                  href="tel:+1"
                  className="inline-flex items-center gap-2 border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
                >
                  <FiPhone className="w-5 h-5" />
                  Call Us
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-4xl font-bold text-emerald-600 mb-1">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    ShedX started with a simple mission: provide quality outdoor buildings at fair prices 
                    with honest, straightforward service. What began as a small operation has grown into one of 
                    the most trusted names in storage buildings and outdoor structures.
                  </p>
                  <p>
                    Our nationwide dealer network means you can find quality sheds near you, 
                    wherever you are. Our team has decades of combined experience in construction and 
                    customer service, ensuring every customer gets expert guidance in choosing the right shed 
                    for their needs.
                  </p>
                  <p>
                    We're proud to offer a wide selection of storage sheds, barns, workshops, studios, and 
                    specialty buildings. Whether you need a simple tool shed or a fully-equipped workshop, 
                    we have options to fit every budget and requirement.
                  </p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="/assets/assets/images/12x24-custom-homeshed.webp"
                    alt="Quality custom storage shed"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-emerald-600 text-white p-6 rounded-xl shadow-lg">
                  <div className="text-3xl font-bold">Since 2010</div>
                  <div className="text-emerald-100">Serving nationwide</div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose ShedX</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                We're committed to providing the best shed buying experience in the country.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 text-center"
                >
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Offer</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                A complete selection of outdoor buildings for every need
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'Storage Sheds', desc: 'Classic storage solutions for lawn equipment, tools, and seasonal items' },
                { name: 'Barn Style', desc: 'Traditional gambrel roof design with maximum overhead storage space' },
                { name: 'Workshops', desc: 'Fully equipped buildings for serious hobbyists and craftsmen' },
                { name: 'Studios', desc: 'Modern designs perfect for home offices, art studios, or she-sheds' },
                { name: 'Garages', desc: 'Full-size buildings with overhead doors for vehicle storage' },
                { name: 'Custom Buildings', desc: 'Work with us to create the perfect building for your specific needs' },
              ].map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiCheck className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-emerald-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-4">Ready to Find Your Perfect Shed?</h2>
              <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
                Visit our lot to see our selection in person, or browse online and give us a call.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/sheds"
                  className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 px-8 py-4 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
                >
                  Browse Inventory
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Location Info */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <FiMapPin className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Visit Our Lot</h3>
                <p className="text-gray-600"><br /> </p>
              </div>
              <div>
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <FiPhone className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Call Us</h3>
                <p className="text-gray-600"></p>
              </div>
              <div>
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <FiMail className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <p className="text-gray-600">sales@shedx.com</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutShed;
